import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { getTrips, getUserData } from '../api/user'
import { getBillingInfo } from '../api/billing'
import { getCards } from '../api/cards'
import { AuthContext } from '../AuthProvider'
import { getVehicles } from '../api/vehicles'
import { getTicketingCatalog, getTickets } from '../api/ticketing'

const DataContext = createContext(null)

export function DataProvider({ children }) {
  const Auth = useContext(AuthContext)
  const [user, setUser] = useState(null)
  const [cards, setCards] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [trips, setTrips] = useState([])
  const [billing, setBilling] = useState([])
  const [catalog, setCatalog] = useState([])
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const firstLoadDone = useRef(false)
  const poller = useRef(null)

  const fetchAll = async (label = 'initial', timeout) => {
    try {
      if (!firstLoadDone.current || label === 'manual') setLoading(true)
      await new Promise(resolve => setTimeout(resolve, timeout))
      console.log(`[FETCH] ${label} @ ${DateTime.local().toISOTime()}`)
      const [fetchedUserData, fetchedCards, fetchedVehicles, fetchedTrips, fetchedBilling, fetchedCatalog, fetchedTickets] = await Promise.all([
        getUserData(),
        getCards(),
        getVehicles(),
        getTrips(),
        getBillingInfo(),
        getTicketingCatalog(),
        getTickets(),
      ])

      setUser(fetchedUserData || {})
      setCards(fetchedCards || [])
      setVehicles(fetchedVehicles || [])
      setTrips(fetchedTrips || [])
      setBilling(fetchedBilling || [])
      setCatalog(fetchedCatalog || [])
      setTickets(fetchedTickets || [])
    } catch (e) {
      console.error('[data] fetch error', e)
    } finally {
      if (!firstLoadDone.current || label === 'manual') {
        firstLoadDone.current = true
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!Auth) return
    fetchAll('initial')
    poller.current = setInterval(() => fetchAll('poll'), 10000)
    return () => {
      if (poller.current) clearInterval(poller.current)
    }
  }, [Auth])

  const value = useMemo(
    () => ({
      loading,
      user,
      cards,
      vehicles,
      trips,
      billing,
      catalog,
      tickets,
      refetch: (duration) => fetchAll('manual', duration),
    }),
    [loading, user, cards, vehicles, trips, billing, catalog, tickets]
  )

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export const useData = () => {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useData must be used within DataProvider')
  return ctx
}

export const useUser = () => {
  const { user, loading, refetch } = useData()
  return { user, loading, refetch }
}

export const useCards = () => {
  const { cards, loading, refetch } = useData()
  return { cards, loading, refetch }
}

export const useVehicles = () => {
  const { vehicles, loading, refetch } = useData()
  return { vehicles, loading, refetch }
}

export const useTrips = (home = false) => {
  const { trips, loading, refetch } = useData()

  const filteredTrips = useMemo(() => {
    if (!Array.isArray(trips)) return []

    return home ? trips.slice(0, 3) : trips
  }, [trips, home])

  return { trips: filteredTrips, loading, refetch }
}

export const useBilling = () => {
  const { billing, loading, refetch } = useData()
  return { billing, loading, refetch }
}

export const useTicketing = () => {
  const { catalog, tickets, loading, refetch } = useData()
  return { catalog, activeTickets: tickets?.active || [], expiredTickets: tickets?.expired || [], loading, refetch }
}
